// Lokaler Speicher auf IndexedDB.
//
// localStorage war für den Zweck zu klein und synchron: der Katalog eines
// Haushalts sprengt schnell die 5-MB-Grenze, und jeder Zugriff blockierte
// den Hauptthread. IndexedDB kann beides besser und überlebt mehr.
import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { ShoppingItem, SyncOp } from '@speis/shared';

interface SpeisDB extends DBSchema {
  /** Zwischenspeicher ganzer Antworten (Liste, Katalog, Plan, Rezepte). */
  cache: {
    key: string;
    value: { key: string; data: unknown; savedAt: number };
  };
  /** Noch nicht gesendete Änderungen. */
  outbox: {
    key: string;
    value: SyncOp & { tries: number; lastError?: string };
    indexes: { 'by-ts': number };
  };
}

let dbPromise: Promise<IDBPDatabase<SpeisDB>> | null = null;

function db(): Promise<IDBPDatabase<SpeisDB>> {
  dbPromise ??= openDB<SpeisDB>('speis', 1, {
    upgrade(database) {
      database.createObjectStore('cache', { keyPath: 'key' });
      const outbox = database.createObjectStore('outbox', { keyPath: 'opId' });
      outbox.createIndex('by-ts', 'ts');
    },
  });
  return dbPromise;
}

// ── Zwischenspeicher ──────────────────────────────────────────────────

export async function cacheGet<T>(key: string): Promise<T | null> {
  try {
    const row = await (await db()).get('cache', key);
    return row ? (row.data as T) : null;
  } catch {
    return null; // Privater Modus o.ä. – die App funktioniert auch ohne Cache.
  }
}

export async function cacheSet(key: string, data: unknown): Promise<void> {
  try {
    await (await db()).put('cache', { key, data, savedAt: Date.now() });
  } catch {
    /* Speicher voll oder gesperrt – nicht kritisch */
  }
}

export async function cacheClear(): Promise<void> {
  try {
    await (await db()).clear('cache');
  } catch {
    /* egal */
  }
}

export const CACHE_KEYS = {
  shopping: 'shopping',
  products: 'products',
  recipes: 'recipes',
  plan: 'plan',
  expiring: 'expiring',
} as const;

/** Bequemer Zugriff auf die zwischengespeicherte Einkaufsliste. */
export function cachedShopping(): Promise<{ items: ShoppingItem[] } | null> {
  return cacheGet(CACHE_KEYS.shopping);
}

// ── Outbox ────────────────────────────────────────────────────────────

export type StoredOp = SyncOp & { tries: number; lastError?: string };

export async function outboxAdd(op: SyncOp): Promise<void> {
  await (await db()).put('outbox', { ...op, tries: 0 });
}

export async function outboxAll(): Promise<StoredOp[]> {
  try {
    return await (await db()).getAllFromIndex('outbox', 'by-ts');
  } catch {
    return [];
  }
}

export async function outboxRemove(opIds: readonly string[]): Promise<void> {
  if (!opIds.length) return;
  const tx = (await db()).transaction('outbox', 'readwrite');
  await Promise.all(opIds.map((id) => tx.store.delete(id)));
  await tx.done;
}

export async function outboxMarkFailed(opId: string, error: string): Promise<void> {
  const store = await db();
  const existing = await store.get('outbox', opId);
  if (!existing) return;
  await store.put('outbox', { ...existing, tries: existing.tries + 1, lastError: error });
}

export async function outboxCount(): Promise<number> {
  try {
    return await (await db()).count('outbox');
  } catch {
    return 0;
  }
}

export async function outboxClear(): Promise<void> {
  try {
    await (await db()).clear('outbox');
  } catch {
    /* egal */
  }
}
